// La riga della visita sulla card dell'immobile (scheda cliente, scheda immobile, lista e home del cliente):
// "Visita sabato 19 · 10:30 con Karolina" (prossima_visita dal server) oppure "Visitato il 19 set · esito: …".
import { MESI, ilGiorno } from './agenda';
import { contesto, dataIn, PRINCIPALE } from '../i18n';

const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];

// La lingua del cliente arriva come ultimo parametro (di base l'italiano: le pagine dello staff non la passano).
// Le date qui sono già ora italiana senza fuso: si scrivono con il fuso del browser, non con Europe/Rome.
const scritta = (lingua, d, opzioni) => dataIn(lingua, d, { ...opzioni, timeZone: undefined });

// Le date delle visite arrivano senza fuso: sono già l'ora italiana
const comeData = (iso) => new Date(String(iso).slice(0, 19));
const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

const giorniTra = (da, a) => Math.round((new Date(a.getFullYear(), a.getMonth(), a.getDate()) - new Date(da.getFullYear(), da.getMonth(), da.getDate())) / 86400000);

// Le date di un altro anno si scrivono con l'anno: "17 agosto 2025", "10 nov 2025". Prima "Aggiunto da voi il 17
// agosto", "Visitato il 10 nov · esito: ci piace", "Vi è piaciuta · visitata il 10 novembre", la storia "17 agosto" e
// le "Già fatte" del calendario sembravano di quest'anno (quandoNovita di utils/percorso.js l'anno lo metteva già).
// Una funzione sola per tutti questi punti: `testo` è la data già scritta, `data` la data vera.
export const conAnno = (testo, data, adesso = new Date()) => {
  const d = data instanceof Date ? data : comeData(data);
  if (!testo || !d || Number.isNaN(d.getTime()) || !adesso || d.getFullYear() === adesso.getFullYear()) return testo;
  return `${testo} ${d.getFullYear()}`;
};

// "sabato 19 · 10:30"; con adesso (pagine del cliente) "oggi · 10:30" e "domani · 10:30", come l'etichetta della card.
// Una visita di un altro anno porta mese e anno ("martedì 12 gen 2027 · 11:00", conAnno): da sola "martedì 12"
// sembrava di quest'anno, e una data già passata l'anno ce l'aveva (giro 8).
export const quandoVisita = (iso, adesso = null, lingua = PRINCIPALE) => {
  const d = comeData(iso);
  const tra = adesso ? giorniTra(adesso, d) : null;
  const riferimento = adesso || new Date();
  const altroAnno = !Number.isNaN(d.getTime()) && d.getFullYear() !== riferimento.getFullYear();
  const { t } = contesto(lingua);
  let giorno;
  if (tra === 0) giorno = t('calendario.oggi');
  else if (tra === 1) giorno = t('calendario.domani');
  else if (lingua === PRINCIPALE) {
    giorno = conAnno(`${GIORNI[d.getDay()]} ${d.getDate()}${altroAnno ? ` ${MESI[d.getMonth()].slice(0, 3)}` : ''}`, d, riferimento);
  } else {
    giorno = scritta(lingua, d, { weekday: 'long', day: 'numeric', ...(altroAnno ? { month: 'short', year: 'numeric' } : {}) });
  }
  return `${giorno} · ${hhmm(d)}`;
};

// Per il cliente "ci piace", come la scheda dell'immobile e il bottone di "Com'è andata?" (prima "vi è piaciuta"
// anche per un trilocale)
export const TESTI_ESITO = {
  staff: { positivo: 'le è piaciuta', negativo: 'non fa per lui', vuoto: 'da dare' },
  cliente: { positivo: 'ci piace', negativo: 'non fa per voi', vuoto: 'in attesa del vostro parere' },
};
// L'esito come lo legge il cliente, nella sua lingua (le stesse parole della scheda e del calendario)
export const testoEsitoCliente = (esito, lingua = PRINCIPALE) => contesto(lingua)
  .t(`immobili.esito.${['positivo', 'negativo'].includes(esito) ? esito : 'vuoto'}`);

const visitato = (u, perCliente, adesso, lingua = PRINCIPALE) => {
  const d = comeData(u.data_visita);
  const esito = perCliente ? testoEsitoCliente(u.esito, lingua) : (TESTI_ESITO.staff[u.esito] || TESTI_ESITO.staff.vuoto);
  const oggi = adesso && giorniTra(adesso, d) === 0;
  const quando = oggi ? contesto(lingua).t('calendario.oggi')
    : lingua === PRINCIPALE ? conAnno(ilGiorno(`${d.getDate()} ${MESI[d.getMonth()].slice(0, 3)}`), d, adesso || undefined)
      : scritta(lingua, d, { day: 'numeric', month: 'short', ...(adesso && d.getFullYear() !== adesso.getFullYear() ? { year: 'numeric' } : {}) });
  return contesto(lingua).t('calendario.visitato', { quando, esito });
};

// Il cliente aveva chiesto di spostarla e l'ora è passata: la visita non c'è stata, si aspetta il nuovo orario
// (prima "Visitato oggi · esito: in attesa del vostro parere" con "Diteci com'è andata")
const TESTI_SPOSTAMENTO = { cliente: 'Spostamento chiesto · in attesa del nuovo orario', staff: 'Spostamento chiesto dal cliente · da rifissare' };

// adesso (pagine del cliente): passata l'ora, la prossima visita diventa "Visitato oggi · esito: …" senza ricaricare.
// visite (facoltativo): l'elenco completo delle visite del cliente, per sapere se una visita passata ha una richiesta
// di spostamento aperta (ultima_visita del server non lo dice)
export const rigaVisita = (immobile, perCliente = false, adesso = null, visite = null, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const spostata = (x) => !!(x.spostamento_richiesto ?? (visite || []).find((v) => v.id === x.id)?.spostamento_richiesto);
  const attesa = (x) => ({ tipo: 'attesa', visita: x, attesaEsito: false,
    testo: perCliente ? t('immobili.scheda.esito_spostamento') : TESTI_SPOSTAMENTO.staff });
  const p = immobile?.prossima_visita;
  if (p && !(adesso && comeData(p.data_visita) <= adesso)) {
    const quando = quandoVisita(p.data_visita, adesso, lingua);
    return { tipo: 'prossima', visita: p,
      testo: p.con ? t('immobili.scheda.visita_futura_con', { quando, nome: p.con }) : t('immobili.scheda.visita_futura', { quando }) };
  }
  if (p) return spostata(p) ? attesa(p) : { tipo: 'fatta', testo: visitato(p, perCliente, adesso, lingua), visita: p, attesaEsito: true };
  const u = immobile?.ultima_visita;
  if (u && !u.esito && spostata(u)) return attesa(u);
  if (u) return { tipo: 'fatta', testo: visitato(u, perCliente, adesso, lingua), visita: u, attesaEsito: !u.esito };
  return null;
};

export const indicazioni = (immobile) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  [immobile?.indirizzo, immobile?.citta].filter(Boolean).join(', ') || immobile?.titolo || '')}`;

// La visita come la vogliono "Sposta" e "Annulla": {id, data, ora, durata, accompagna_id, immobile_id, testo}
export const visitaDa = (prossima, immobileId) => {
  const d = comeData(prossima.data_visita);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { id: prossima.id, data: iso, ora: hhmm(d), durata: prossima.durata || 60, accompagna_id: prossima.accompagna_id || null,
    immobile_id: immobileId, testo: quandoVisita(prossima.data_visita) };
};

// Da una voce dell'agenda (tipo visita)
export const visitaDaVoce = (v) => ({ id: v.rif?.visita_id, data: v.data, ora: v.ora, durata: v.dettaglio?.durata || 60,
  accompagna_id: v.assegnata_a || null, immobile_id: v.immobile_id, cliente_id: v.cliente_id, cliente_nome: v.cliente_nome || v.cliente_breve,
  testo: `${v.immobile?.indirizzo || v.immobile?.titolo || ''} · ${v.ora || ''}`.trim() });
